"use client";

import { type ReactNode, useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { useAppContext } from "../../context/AppContext";
import type { ShellConfig, PageMeta, QuickAction } from "../../types";
import { cn } from "../../utils/cn";
import { getPageMeta } from "../../utils/getPageMeta";

import { FloatingActionButton } from "./FloatingActionButton";
import { LeftSidebar } from "./LeftSidebar";
import { MobileSidebarOverlay } from "./MobileSidebarOverlay";
import { TopHeader } from "./TopHeader";

type ShellLayoutProps = {
  children: ReactNode;
  locale: string;
  config: ShellConfig;
  routeMap: Record<string, PageMeta>;
  quickActions?: QuickAction[];
};

export function ShellLayout({
  children,
  locale,
  config,
  routeMap,
  quickActions,
}: ShellLayoutProps) {
  const { sidebarCollapsed } = useAppContext();
  const pathname = usePathname();
  const t = useTranslations();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const pageMeta = getPageMeta(pathname, locale, routeMap);
    document.title = `Dashboard - ${t(pageMeta.titleKey)}`;
  }, [pathname, locale, routeMap, t]);

  const toggleMobile = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <div className="flex h-screen bg-surface">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden shrink-0 shadow-shell-sidebar transition-all duration-shell ease-shell lg:block",
          sidebarCollapsed ? "w-[72px]" : "w-[260px]",
        )}
      >
        <LeftSidebar
          locale={locale}
          brand={config.brand}
          navItems={config.navItems}
          locales={config.locales}
          languageLabels={config.languageLabels}
        />
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopHeader
          locale={locale}
          headerActions={config.headerActions}
          routeMap={routeMap}
          onBurgerClick={toggleMobile}
          headerExtra={config.headerExtra}
          onManageSubscription={config.onManageSubscription}
        />
        <main className="shell-scrollbar flex-1 overflow-y-auto bg-content-bg">
          {children}
          {quickActions && quickActions.length > 0 && (
            <FloatingActionButton actions={quickActions} locale={locale} />
          )}
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      <MobileSidebarOverlay
        open={mobileOpen}
        onClose={closeMobile}
        locale={locale}
        brand={config.brand}
        navItems={config.navItems}
        locales={config.locales}
        languageLabels={config.languageLabels}
      />
    </div>
  );
}
